import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useAppSelector } from '@pkg/hooks/redux';
import { type SupportedNetworkName } from '@pkg/types';

import { type WishlistLineupItem } from '@app/client/common/structs';
import { WISHLIST_PORTFOLIO_SUMMARY_CACHE } from '@app/client/resources/fantasy';
import { callGetLineupSummary } from '@app/client/resources/wishlists';
import { useFantasyMetadataStore } from '@app/store/zustand';

export interface LineupItemsFormat extends Partial<WishlistLineupItem> {
    slug: string;
    quantity: number;
    floorPrice?: string;
    valueUsd?: string;
    initialValueUsd?: string;
    confirmed: boolean | undefined;
    chain: SupportedNetworkName;
}

type UseGetWishlistSummaryDataProps = {
    providedSlug: string;
    isCurrentUserLineup?: boolean;
    getCurrentLineup?: boolean;
    disabled?: boolean;
    intervalEnabled?: boolean;
    ignorePreviousData?: boolean;
};
export const useGetLineupSummaryData = ({
    providedSlug,
    disabled,
    ignorePreviousData = false,
    intervalEnabled = false,
}: UseGetWishlistSummaryDataProps) => {
    const { currentGame } = useFantasyMetadataStore();
    const userId = useAppSelector((state) => state.user.userAccount?.user.id);
    const enabled = Boolean(providedSlug?.length) && Boolean(currentGame?.id) && !disabled;
    const query = currentGame?.id ? { competition_id: currentGame.id } : undefined;
    const cache = [WISHLIST_PORTFOLIO_SUMMARY_CACHE, providedSlug, userId];
    return useQuery({
        queryKey: cache,
        queryFn: ({ signal }) =>
            callGetLineupSummary({
                slug: providedSlug,
                query,
                settings: { signal },
            }),
        retry: false,
        enabled: enabled,
        placeholderData: ignorePreviousData ? undefined : keepPreviousData,
        select: (res) => {
            if (res?.ok && res?.value) {
                return res?.value;
            }
        },
        refetchInterval: intervalEnabled ? 5000 : undefined,
    });
};
